import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown } from 'react-icons/fi';
import { Helmet } from 'react-helmet';

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const faqData = [
    {
      question: "What roofing services do you offer?",
      answer: "We offer a comprehensive range of roofing services including residential and commercial roof installation, repair, maintenance, emergency services, and inspections. Our team is experienced with all types of roofing materials including shingles, metal, tile, and flat roofing systems."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes, we are fully licensed and insured. We carry comprehensive liability insurance and workers' compensation coverage to protect both our clients and employees. Our license number is available upon request."
    },
    {
      question: "How long does a typical roof installation take?",
      answer: "A typical residential roof installation usually takes 1-3 days, depending on the size and complexity of the project. Commercial projects may take longer. Weather conditions can also affect the timeline. We'll provide you with a specific estimate for your project during the consultation."
    },
    {
      question: "Do you offer emergency roofing services?",
      answer: "Yes, we provide 24/7 emergency roofing services. If you have a leak, storm damage, or any other urgent roofing issue, contact our emergency line immediately. We aim to respond within 2 hours for emergency situations."
    },
    {
      question: "What warranties do you offer?",
      answer: "We offer manufacturer warranties on materials (typically 25-50 years) and our own workmanship warranty (typically 10 years). The specific warranty terms depend on the materials and type of installation. We'll provide detailed warranty information during your consultation."
    },
    {
      question: "How do you handle project estimates?",
      answer: "We provide free, detailed written estimates after a thorough inspection of your property. Our estimates include material costs, labor, timeline, and any potential additional work that might be needed. We're transparent about pricing and won't surprise you with hidden fees."
    },
    {
      question: "What areas do you serve?",
      answer: "We serve the greater metropolitan area and surrounding communities within a 50-mile radius. Contact us with your location for specific service availability."
    }
  ];

  return (
    <>
      <Helmet>
        <title>FAQ | Xtreme Roofing & Construction</title>
        <meta name="description" content="Find answers to common questions about our roofing and construction services, warranties, and payment options." />
      </Helmet>

      <FAQContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <Title>Frequently Asked Questions</Title>
        <Description>
          Find answers to common questions about our roofing and construction services. 
          Can't find what you're looking for? Contact us directly.
        </Description>

        <FAQList>
          {faqData.map((faq, index) => (
            <FAQItem key={index}>
              <QuestionButton
                onClick={() => setActiveIndex(activeIndex === index ? null : index)}
                active={activeIndex === index}
              >
                <QuestionText>{faq.question}</QuestionText>
                <IconWrapper active={activeIndex === index}>
                  <FiChevronDown />
                </IconWrapper>
              </QuestionButton>

              <AnimatePresence>
                {activeIndex === index && (
                  <AnswerContainer
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Answer>{faq.answer}</Answer>
                  </AnswerContainer>
                )}
              </AnimatePresence>
            </FAQItem>
          ))}
        </FAQList>

        <ContactSection>
          <ContactTitle>Still have questions?</ContactTitle>
          <ContactText>
            Our team is here to help. Contact us for personalized assistance.
          </ContactText>
          <ContactButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.href = '/contact'}
          >
            Contact Us
          </ContactButton>
        </ContactSection>
      </FAQContainer>
    </>
  );
};

const FAQContainer = styled(motion.div)`
  max-width: 800px;
  margin: 4rem auto;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 2.5rem);
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

const Description = styled.p`
  text-align: center;
  color: var(--text-light);
  margin-bottom: 3rem;
  font-size: 1.1rem;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FAQItem = styled.div`
  background: var(--background-light);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  overflow: hidden;
`;

const QuestionButton = styled.button`
  width: 100%;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--text);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 0, 0.1);
  }
`;

const QuestionText = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  text-align: left;
`;

const IconWrapper = styled.span`
  transform: rotate(${props => props.active ? '180deg' : '0deg'});
  transition: transform 0.3s ease;
  color: var(--primary);
`;

const AnswerContainer = styled(motion.div)`
  overflow: hidden;
`;

const Answer = styled.div`
  padding: 0 1.5rem 1.5rem;
  color: var(--text-light);
  line-height: 1.6;
`;

const ContactSection = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
  background: var(--background-light);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
`;

const ContactTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--text);
`;

const ContactText = styled.p`
  color: var(--text-light);
  margin-bottom: 1.5rem;
`;

const ContactButton = styled(motion.button)`
  padding: 0.8rem 2rem;
  background: var(--primary);
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: var(--primary-light);
  }
`;

export default FAQ; 